import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { IoTrashOutline, IoClose, IoShieldCheckmarkOutline } from 'react-icons/io5';
import { FaUserGraduate, FaChalkboardTeacher } from 'react-icons/fa';
import { alpha } from '@mui/material';
import { CohortWithDetails } from 'src/types/cohort.types';

interface DeleteCohortModalProps {
  open: boolean;
  onClose: () => void;
  cohort: CohortWithDetails | null;
  onConfirmDelete: (cohortId: string) => Promise<void>;
  isDeleting?: boolean;
}

export function DeleteCohortModal({
  open,
  onClose,
  cohort,
  onConfirmDelete,
  isDeleting = false,
}: DeleteCohortModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setAcknowledged(false);
    }
  }, [open]);

  if (!cohort) return null;

  const scholarCount = cohort.students?.length ?? cohort.studentCount ?? 0;
  const teacherCount = cohort.teachers?.length ?? 0;
  const totalBalance = (cohort.students || []).reduce((sum, s) => sum + (s.balance || 0), 0);

  const handleDelete = async () => {
    await onConfirmDelete(cohort.id);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isDeleting && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid #FECACA',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: '#FEE2E2',
                color: '#DC2626',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <IoTrashOutline size={22} />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={800} color="#0F172A">
                  Delete Cohort Section
                </Typography>
                <Chip
                  label="Destructive"
                  size="small"
                  sx={{
                    bgcolor: '#FEF2F2',
                    color: '#DC2626',
                    fontWeight: 700,
                    fontSize: '0.6875rem',
                    height: 20,
                    border: '1px solid #FECACA',
                  }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                You are about to permanently delete <strong>{cohort.name}</strong> ({cohort.className || 'Grade Tier'}).
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={isDeleting} sx={{ color: '#94A3B8' }}>
            <IoClose size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Impact Summary Cards */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="#64748B" letterSpacing={0.5} sx={{ display: 'block', mb: 1 }}>
              IMPACT ON ENROLLED USERS
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.25,
                      bgcolor: alpha('#3B82F6', 0.1),
                      color: '#2563EB',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <FaUserGraduate size={13} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                      {scholarCount} Scholars
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                      Will become unassigned
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.25,
                      bgcolor: alpha('#D97706', 0.1),
                      color: '#D97706',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <FaChalkboardTeacher size={13} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                      {teacherCount} Educators
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                      Assignments unlinked
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Zero Data Loss Guarantee */}
          <Box
            sx={{
              p: 1.75,
              borderRadius: 2,
              bgcolor: '#F0FDF4',
              border: '1px solid #BBF7D0',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box sx={{ color: '#16A34A', mt: 0.25 }}>
                <IoShieldCheckmarkOutline size={20} />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={800} color="#166534" fontSize="0.8125rem">
                  Financial Data & Balances Remain 100% Safe
                </Typography>
                <Typography variant="caption" color="#15803D" fontSize="0.75rem" lineHeight={1.4} sx={{ display: 'block', mt: 0.25 }}>
                  Student virtual accounts, transaction histories, Street Cents, and individual balances (${totalBalance.toLocaleString()} total) will remain intact. No financial or student records will be deleted.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Acknowledgment Checkbox */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#FEF2F2',
              border: '1px solid #FECACA',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  sx={{
                    color: '#DC2626',
                    '&.Mui-checked': { color: '#DC2626' },
                    py: 0.5,
                  }}
                />
              }
              label={
                <Typography variant="body2" color="#991B1B" fontWeight={600} fontSize="0.8125rem">
                  I understand that deleting section <strong>{cohort.name}</strong> will unassign all enrolled scholars and cannot be undone.
                </Typography>
              }
              sx={{ m: 0, alignItems: 'flex-start' }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          disabled={isDeleting}
          sx={{
            color: '#64748B',
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'none',
            '&:hover': { bgcolor: '#F1F5F9', color: '#334155' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!acknowledged || isDeleting}
          onClick={handleDelete}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <IoTrashOutline size={16} />}
          sx={{
            fontWeight: 700,
            fontSize: '0.8125rem',
            px: 2.5,
            py: 0.8,
            borderRadius: 2,
            textTransform: 'none',
            bgcolor: '#DC2626',
            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
            '&:hover': { bgcolor: '#B91C1C' },
          }}
        >
          {isDeleting ? 'Deleting Cohort...' : `Permanently Delete ${cohort.name}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
